import React, { useMemo, useEffect, Suspense, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Route, Switch, Redirect } from 'react-router-dom'

import Loading from '../../components/Loading'
import { ErrorReloading } from '../../components/Error'
import Header from '../../components/Header'
import MapPanel from '../../components/MapPanel'
import Main from '../../components/Main'
import Sidebar from '../../components/Sidebar'
import MapViewFallBack from '../../components/MapViewFallBack'
import ProfileViewFallBack from '../../components/ProfileView'
import InfoPanelView from '../../components/InfoPanelView'
import AccommodationsOrderControl from '../../components/AccommodationsOrderControl'
import MapManager from '../../components/MapManager'
import ExportPanel from '../../components/ExportPanel'
import {
  RouteLayer,
  SyncProfileControl
} from '../../components/Map'

import Page404 from '../404Page'
import RoutePage from './RoutePage'
import TrailPage from './TrailPage'
import AccommodationsPage from './AccommodationsPage'

import { useUrlParams, useNavigatorOnline } from '../../hooks'
import { useRoute } from '../../api'
import { reverse } from '../../urls'
import { useUserPosition, useUserIsOnTrail } from '../../slices/userposition'
import { showSplashScreen } from '../../slices/ui'
import { useMapHighlightTrailId } from '../../slices/map'

import { retry } from '../../utils'

const MapView = React.lazy(() => retry(
  () => import('../../components/MapView'), 
  { fallback: ErrorReloading }
))
const ProfileView = React.lazy(() => retry(
  () => import('../../components/ProfileView'), 
  { fallback: ErrorReloading }
))
const AccommodationsAvailabilityControl = React.lazy(() => retry(
  () => import('../../components/AccommodationsAvailabilityControl'), 
  { fallback: ErrorReloading }
))

const MapViewer = (props) => {
  const { route_id, trail_id } = useUrlParams(['route_id', 'trail_id'])
  const highlightTrailId = useMapHighlightTrailId()
  const userPosition = useUserPosition()
  const userIsOnTrail = useUserIsOnTrail()
  const offset = (userPosition && userIsOnTrail) ? userPosition.distance : null

  return (
    <Suspense fallback={<MapViewFallBack/>}>
        <MapView>
          <RouteLayer route_id={route_id} trail_id={trail_id}
                      highlight_id={highlightTrailId}
                      offsetDistance={offset} />
          <SyncProfileControl route_id={route_id} trail_id={trail_id} />
        </MapView>
    </Suspense>
  )
}

const Viewer = (props) => {
  const { isOnline } = useNavigatorOnline()
  const { route_id } = useUrlParams(['route_id'])
  const route = useRoute({ routeId: route_id })
  const dispach = useDispatch()
  const trails_count = route ? route.trails.length : undefined
  const routeIsLoad = !!route
  const redirectTimeout = useRef(null)

  useEffect(() => {
    clearTimeout(redirectTimeout.current)
    if (routeIsLoad || (!routeIsLoad && !isOnline)) {
      dispach(showSplashScreen(false))
    } else {
      dispach(showSplashScreen(true))
      redirectTimeout.current = setTimeout(() => {
        window.location.reload(true)
      }, 20000)
    }
    return () => {
      clearTimeout(redirectTimeout.current)
      dispach(showSplashScreen(false))
    }
  }, [routeIsLoad, isOnline, dispach])

  return useMemo(() => {
    return (<>
      <Header className="mapview"></Header>

      <Main className="mapview">

        { trails_count === undefined
          ? (<Loading/>)
          : (
          <Switch>
            { trails_count === 1 && (
              <Redirect exact from={reverse(':route_id')} to={reverse(':route_id', 1)} />
            )}
            { trails_count > 1 && (
              <Route exact path={reverse(':route_id')} >
                <RoutePage/>
              </Route>
            )}
            <Route exact path={reverse(':route_id', ':trail_id')}>
              <TrailPage/>
            </Route>
            <Route exact path={reverse(':route_id', null, ':accommodationList')}>
              <AccommodationsPage />
            </Route>
            <Route exact path={reverse(':route_id', ':trail_id', ':accommodationList')}>
              <AccommodationsPage />
            </Route>
            <Route path="*">
              <Page404 />
            </Route>
          </Switch>
            )}

      </Main>

      <MapPanel>
        <MapViewer/>
        <Suspense fallback={<ProfileViewFallBack/>}>
          <ProfileView/>
        </Suspense>
        <InfoPanelView/>
      </MapPanel>

      <Route exact path={[
        reverse(':route_id', null, ':accommodationList'),
        reverse(':route_id', ':trail_id', ':accommodationList')
      ]}>
        <Sidebar name="accommodationorder" position="right">
          <AccommodationsOrderControl/>
        </Sidebar>
      </Route>

      <Sidebar name="reservation" position="right">
        <Suspense fallback={<Loading/>}>
          <AccommodationsAvailabilityControl/>
        </Suspense>
      </Sidebar>

      <MapManager/>
      <ExportPanel/>
    </>
    )
  }, [trails_count])
}

export default Viewer
